import type {
  AxiosError,
  InternalAxiosRequestConfig} from 'axios'
import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import qs from 'qs'

import { QS_CONFIG } from '../../constants/api'
import { addDisplayPlatform } from '../platform'
import { addCredentialsToRequest } from './addCredentialsToRequest'
import { handleFetchError } from './fetchError'
import { getApiOrigin } from './getApiOrigin'
import { refreshAuthToken } from './refreshAuthToken'

export type RequestPath = `/${string}`

type RequestBody = Record<string, unknown>

const DEFAULT_HEADERS = {
  'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
  "Accept": 'application/json',
}

axios.interceptors.request.use(
  (req: InternalAxiosRequestConfig) => addCredentialsToRequest(req),
  (error: AxiosError) => Promise.reject(error),
)

axios.interceptors.response.use(
  res => res,
  (error: AxiosError) => refreshAuthToken(error),
)

// There is no trycatch because react-query will handle it
async function http<ResponseData, RequestData>(
  path: RequestPath,
  axiosRequestConfig: AxiosRequestConfig<RequestData>,
) {
  const headers = {
    ...DEFAULT_HEADERS,
    ...(axiosRequestConfig?.headers ? axiosRequestConfig.headers : {}),
  }

  // Remove undefined headers
  Object.keys(headers).forEach(key => {
    const headerKey = key as keyof typeof headers
    if (!headers[headerKey]) {
      delete headers[headerKey]
    }
  })

  // Two URL shapes are in flight here:
  //   `path`             — the original caller-supplied URL (still has /v3)
  //   `path.replace(...)` — what axios actually sends (no /v3 prefix)
  // getApiOrigin needs the ORIGINAL URL so isApi3 can detect /v3 and route
  // to VITE_API_URL_3. Outgoing axios then uses the stripped URL because
  // the API3 backend doesn't expect /v3 in its path. Don't unify them
  // until every PHP endpoint is migrated to Java.
  const config = {
    ...axiosRequestConfig,
    url: path.replace(/^\/v3/, ''), // TODO: Remove this once all PHP endpoints are migrated to Java
    headers,
  }

  const axiosRequest: AxiosRequestConfig<RequestData> = {
    baseURL: getApiOrigin({
      ...config,
      url: path,
    }),
    timeout: 30_000, // default so stalled connections don't hang forever; callers can override via config
    ...config,
  }

  let axiosResponse: AxiosResponse<ResponseData, RequestData>
  try {
    axiosResponse = await axios(axiosRequest)
  } catch (error) {
    // HTTP-status failures keep surfacing as FetchClientError/FetchServerError;
    // network-level failures (offline, timeout, abort) rethrow the AxiosError
    // itself so callers see a rejection instead of a response with no data.
    handleFetchError<RequestData>(error as AxiosError, axiosRequest)
    throw error
  }

  return axiosResponse
}

async function get<ResponseData>(
  path: RequestPath,
  params?: Record<string, unknown>,
  axiosRequestConfig?: AxiosRequestConfig<string>,
) {
  let stringParams = ''
  if (params) {
    // Type assertion is added here so that we can still pass number | string
    // using Record<string, unknown> in GetArgs
    stringParams = new URLSearchParams(params as Record<string, string>).toString()
  }

  // Add display_platform in payload
  const fullPath = addDisplayPlatform(path, stringParams) as RequestPath

  return await http<ResponseData, string>(withTypeQuery(fullPath, params), {
    method: 'GET',
    ...axiosRequestConfig,
  })
}

async function post<ResponseData>(
  path: RequestPath,
  body?: RequestBody,
  axiosRequestConfig?: AxiosRequestConfig<string>,
) {
  return await http<ResponseData, string>(withTypeQuery(path, body), {
    method: 'POST',
    data: qs.stringify(body, QS_CONFIG),
    ...axiosRequestConfig,
  })
}

// Mirror the operation type onto the URL as a `_type` query param so the
// multiplexed PHP endpoint (`/request.php`, `/center.php`, …) is observable
// from the URL alone — the discriminator otherwise lives only inside the
// urlencoded form body (or, for GET, alongside the rest of the querystring).
// Reads from `submit_type` first, then `type`. Exported as a test seam.
export function withTypeQuery(path: RequestPath, body?: RequestBody): RequestPath {
  if (!body) return path
  const typeValue = body.submit_type ?? body.type
  if (!typeValue) return path
  if (typeof typeValue !== 'string' && typeof typeValue !== 'number') return path
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}_type=${encodeURIComponent(String(typeValue))}` as RequestPath
}

async function put<ResponseData>(
  path: RequestPath,
  body?: RequestBody,
  axiosRequestConfig?: AxiosRequestConfig<string>,
) {
  return await http<ResponseData, string>(withTypeQuery(path, body), {
    method: 'PUT',
    data: qs.stringify(body, QS_CONFIG),
    ...axiosRequestConfig,
  })
}

async function deleteMethod<ResponseData>(
  path: RequestPath,
  body?: RequestBody,
  axiosRequestConfig?: AxiosRequestConfig<string>,
) {
  return await http<ResponseData, string>(withTypeQuery(path, body), {
    method: 'DELETE',
    data: qs.stringify(body, QS_CONFIG),
    ...axiosRequestConfig,
  })
}

async function patch<ResponseData>(
  path: RequestPath,
  body?: RequestBody,
  axiosRequestConfig?: AxiosRequestConfig<string>,
) {
  return await http<ResponseData, string>(withTypeQuery(path, body), {
    method: 'PATCH',
    data: qs.stringify(body, QS_CONFIG),
    ...axiosRequestConfig,
  })
}

async function uploadMedia<ResponseData>(
  path: RequestPath,
  body: RequestBody,
  axiosRequestConfig?: AxiosRequestConfig<FormData>,
) {
  const formData = new FormData()
  for (const key in body) {
    if (Array.isArray(body[key])) {
      (body[key] as File[]).forEach(file => {
        formData.append(`${key}[]`, file)
      })
    } else {
      formData.append(key, body[key] as string | Blob)
    }
  }
  return await http<ResponseData, FormData>(path, {
    method: 'POST',
    data: formData,
    // We need to have empty headers for using formdata in media upload
    // See this article: https://muffinman.io/blog/uploading-files-using-fetch-multipart-form-data/
    headers: undefined,
    ...axiosRequestConfig,
  })
}

export const api = {
  http,
  get,
  post,
  put,
  delete: deleteMethod,
  patch,
  uploadMedia,
}
